import type { ActiveFilter } from '@oysterjs/ui/search-filter';
import { type FilterDefinition } from '@oysterjs/ui/search-filter';
import {
  BaseDateFilter,
  BaseSearchFilter,
  BaseSharedWithMeFilter,
  BaseTeamFilter,
  BaseUserFilter,
  BaseVisibilityFilter,
  type FilterViewer,
  getSinceDate,
  matchesVisibilityFilter
} from '~/api/data/filter-base';
import type { CustomPresetFieldsFragment } from '~/api/graphql';

export type PresetFilter = 'search' | 'user' | 'date' | 'visibility' | 'sharedWithMe' | 'team';

function presetMatchesFilter(
  preset: CustomPresetFieldsFragment,
  filter: ActiveFilter<PresetFilter>,
  viewer: FilterViewer
): boolean {
  const visibilityMatch = matchesVisibilityFilter(preset, filter, viewer);
  if (visibilityMatch !== null) return visibilityMatch;
  switch (filter.type) {
    case 'search': {
      const term = (filter.value as string).toLowerCase();
      return (
        preset.title.toLowerCase().includes(term) ||
        // createdBy is null for a cross-agency shared preset (agency-level attribution).
        (preset.createdBy?.name.toLowerCase().includes(term) ?? false)
      );
    }
    case 'user':
      return preset.createdBy?.id === filter.value;
    case 'date': {
      const since = getSinceDate(filter.value as string);
      if (since == null) return true;
      return preset.updatedAt != null && new Date(preset.updatedAt) >= since;
    }
    default:
      return true;
  }
}

export function filterPresets(
  presets: CustomPresetFieldsFragment[],
  activeFilters: ActiveFilter<PresetFilter>[],
  viewer: FilterViewer
): CustomPresetFieldsFragment[] {
  if (activeFilters.length === 0) return presets;
  return presets.filter((p) => activeFilters.every((f) => presetMatchesFilter(p, f, viewer)));
}

export class PresetUserFilter extends BaseUserFilter<PresetFilter> {
  constructor() {
    super('user');
  }
}

export class PresetDateFilter extends BaseDateFilter<PresetFilter> {
  constructor() {
    super('date');
  }
}

export class PresetSearchFilter extends BaseSearchFilter<PresetFilter> {
  constructor() {
    super('search');
  }
}

export class PresetVisibilityFilter extends BaseVisibilityFilter<PresetFilter> {
  constructor() {
    super('visibility');
  }
}

export class PresetSharedWithMeFilter extends BaseSharedWithMeFilter<PresetFilter> {
  constructor() {
    super('sharedWithMe');
  }
}

export class PresetTeamFilter extends BaseTeamFilter<PresetFilter> {
  constructor() {
    super('team');
  }
}

export const createPresetFilterDefinitions = (): FilterDefinition<PresetFilter>[] => [
  new PresetUserFilter(),
  new PresetDateFilter(),
  new PresetSearchFilter(),
  new PresetVisibilityFilter(),
  new PresetSharedWithMeFilter(),
  new PresetTeamFilter()
];
